import React, { useState, useEffect } from 'react';

// Breakthrough Game Component
// Two players each have 16 pieces on an 8×8 board.
// White (Player 1) occupies rows 6–7 (bottom); Black (Player 2) occupies rows 0–1 (top).
// A piece may move one square straight forward or diagonally forward.
// Diagonal moves may capture an opponent piece; straight-forward moves only go to empty squares.
// A player wins by reaching the opponent's back row, capturing all opponent pieces,
// or leaving the opponent with no valid moves.

const BOARD_SIZE = 8;

const createInitialBoard = () => {
	const board = Array(BOARD_SIZE * BOARD_SIZE).fill(null);
	for (let col = 0; col < BOARD_SIZE; col++) {
		board[0 * BOARD_SIZE + col] = 'black'; // row 0
		board[1 * BOARD_SIZE + col] = 'black'; // row 1
		board[6 * BOARD_SIZE + col] = 'white'; // row 6
		board[7 * BOARD_SIZE + col] = 'white'; // row 7
	}
	return board;
};

/**
 * Returns all valid destination indices for a piece at `from` on `board`.
 * White moves upward (decreasing row); Black moves downward (increasing row).
 * Straight-forward moves only reach empty squares.
 * Diagonal-forward moves reach empty squares OR opponent squares (capture).
 */
const getValidMovesForPiece = (from, board) => {
	const piece = board[from];
	if (!piece) return [];

	const row = Math.floor(from / BOARD_SIZE);
	const col = from % BOARD_SIZE;
	const opponent = piece === 'white' ? 'black' : 'white';
	// White moves up (dRow = -1); Black moves down (dRow = +1)
	const dRow = piece === 'white' ? -1 : 1;
	const moves = [];

	// Three forward directions: straight, diagonal-left, diagonal-right
	for (const dCol of [-1, 0, 1]) {
		const newRow = row + dRow;
		const newCol = col + dCol;
		if (newRow < 0 || newRow >= BOARD_SIZE || newCol < 0 || newCol >= BOARD_SIZE) continue;
		const target = board[newRow * BOARD_SIZE + newCol];
		if (dCol === 0) {
			// Straight forward: only to empty squares
			if (target === null) moves.push(newRow * BOARD_SIZE + newCol);
		} else {
			// Diagonal: empty square or opponent piece (capture)
			if (target === null || target === opponent) moves.push(newRow * BOARD_SIZE + newCol);
		}
	}

	return moves;
};

/**
 * Returns true if `player` has at least one valid move on `board`.
 */
const hasValidMoves = (player, board) => {
	for (let i = 0; i < board.length; i++) {
		if (board[i] === player && getValidMovesForPiece(i, board).length > 0) {
			return true;
		}
	}
	return false;
};

/**
 * Collect all { from, to } moves for `player` on `board`.
 */
const getAllMoves = (player, board) => {
	const moves = [];
	for (let i = 0; i < board.length; i++) {
		if (board[i] === player) {
			for (const to of getValidMovesForPiece(i, board)) {
				moves.push({ from: i, to });
			}
		}
	}
	return moves;
};

/**
 * Check if `player` has won after moving a piece to `toIndex` on `board`.
 * Win conditions:
 *  - Reached opponent's back row
 *  - Opponent has no pieces remaining
 *  - Opponent has no valid moves
 */
const checkWin = (player, toIndex, board) => {
	const row = Math.floor(toIndex / BOARD_SIZE);
	const opponent = player === 'white' ? 'black' : 'white';

	// Reached opponent's back row
	if (player === 'white' && row === 0) return true;
	if (player === 'black' && row === BOARD_SIZE - 1) return true;

	// Opponent has no pieces
	if (!board.some(c => c === opponent)) return true;

	// Opponent has no valid moves
	if (!hasValidMoves(opponent, board)) return true;

	return false;
};

// ── Component ─────────────────────────────────────────────────────────────────

const Breakthrough = () => {
	const [gameStarted, setGameStarted] = useState(false);
	const [playerTypes, setPlayerTypes] = useState({ white: 'human', black: 'human' });
	const [board, setBoard] = useState(createInitialBoard);
	const [currentPlayer, setCurrentPlayer] = useState('white');
	const [selectedPiece, setSelectedPiece] = useState(null); // index
	const [validMoveSet, setValidMoveSet] = useState(new Set());
	const [winner, setWinner] = useState(null);

	// ── Helpers ────────────────────────────────────────────────────────────────

	const resetGame = () => {
		setBoard(createInitialBoard());
		setCurrentPlayer('white');
		setSelectedPiece(null);
		setValidMoveSet(new Set());
		setWinner(null);
		setGameStarted(false);
	};

	const startGame = () => setGameStarted(true);

	const handlePlayerTypeChange = (player, type) => {
		setPlayerTypes(prev => ({ ...prev, [player]: type }));
	};

	// ── Human click handler ────────────────────────────────────────────────────

	const handleClick = (idx) => {
		if (winner || !gameStarted) return;
		if (playerTypes[currentPlayer] !== 'human') return;

		if (selectedPiece === null) {
			// Select a piece belonging to the current player
			if (board[idx] === currentPlayer) {
				const moves = getValidMovesForPiece(idx, board);
				if (moves.length > 0) {
					setSelectedPiece(idx);
					setValidMoveSet(new Set(moves));
				}
			}
		} else if (idx === selectedPiece) {
			// Deselect
			setSelectedPiece(null);
			setValidMoveSet(new Set());
		} else if (validMoveSet.has(idx)) {
			// Perform move
			const newBoard = [...board];
			newBoard[idx] = currentPlayer;
			newBoard[selectedPiece] = null;
			setBoard(newBoard);
			setSelectedPiece(null);
			setValidMoveSet(new Set());

			if (checkWin(currentPlayer, idx, newBoard)) {
				setWinner(currentPlayer);
			} else {
				setCurrentPlayer(currentPlayer === 'white' ? 'black' : 'white');
			}
		} else if (board[idx] === currentPlayer) {
			// Switch selection to another own piece
			const moves = getValidMovesForPiece(idx, board);
			if (moves.length > 0) {
				setSelectedPiece(idx);
				setValidMoveSet(new Set(moves));
			}
		}
	};

	// ── Computer player ────────────────────────────────────────────────────────

	useEffect(() => {
		if (!gameStarted || winner) return;
		if (playerTypes[currentPlayer] !== 'computer') return;

		const timeoutId = setTimeout(() => {
			const moves = getAllMoves(currentPlayer, board);
			if (moves.length === 0) {
				setWinner(currentPlayer === 'white' ? 'black' : 'white');
				return;
			}
			const { from, to } = moves[Math.floor(Math.random() * moves.length)];
			const newBoard = [...board];
			newBoard[to] = currentPlayer;
			newBoard[from] = null;
			setBoard(newBoard);

			if (checkWin(currentPlayer, to, newBoard)) {
				setWinner(currentPlayer);
			} else {
				setCurrentPlayer(currentPlayer === 'white' ? 'black' : 'white');
			}
		}, 500);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, gameStarted, winner, board, playerTypes]);

	// ── Status message ─────────────────────────────────────────────────────────

	const getStatus = () => {
		if (winner) {
			const name = winner === 'white' ? 'White' : 'Black';
			return `Winner: ${name}!`;
		}
		const name = currentPlayer === 'white' ? 'White' : 'Black';
		return `Current Turn: ${name}`;
	};

	// ── Configuration screen ───────────────────────────────────────────────────

	if (!gameStarted) {
		return (
			<div className="game-container">
				<h1 className="game-title">Breakthrough</h1>
				<h2 className="config-subtitle">Player Configuration</h2>
				<div className="player-config">
					<div className="player-config-item">
						<h3>White (moves first)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes.white === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('white', 'human')}
							>
								Human
							</button>
							<button
								className={`player-type-button ${playerTypes.white === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('white', 'computer')}
							>
								Computer
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Black</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes.black === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('black', 'human')}
							>
								Human
							</button>
							<button
								className={`player-type-button ${playerTypes.black === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('black', 'computer')}
							>
								Computer
							</button>
						</div>
					</div>
				</div>
				<button className="new-game-button" onClick={startGame}>
					Start Game
				</button>
			</div>
		);
	}

	// ── Board rendering ────────────────────────────────────────────────────────

	const CELL = 100; // SVG units per cell
	const VIEWBOX = BOARD_SIZE * CELL;
	const isHumanTurn = playerTypes[currentPlayer] === 'human';

	return (
		<div className="game-container">
			<h1 className="game-title">Breakthrough</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			<svg
				viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
				className="breakthrough-svg"
			>
				{board.map((cell, idx) => {
					const row = Math.floor(idx / BOARD_SIZE);
					const col = idx % BOARD_SIZE;
					const x = col * CELL;
					const y = row * CELL;

					const isLight = (row + col) % 2 === 0;
					const isSelected = idx === selectedPiece;
					const isValid = validMoveSet.has(idx);

					let squareFill;
					if (isSelected) {
						squareFill = '#86efac'; // green highlight for selected piece
					} else if (isValid) {
						squareFill = '#fde68a'; // yellow hint for valid destinations
					} else if (isLight) {
						squareFill = '#f0d9b5';
					} else {
						squareFill = '#b58863';
					}

					const isClickable = !winner && isHumanTurn && (
						(selectedPiece === null && cell === currentPlayer) ||
						(selectedPiece !== null && (isValid || (cell === currentPlayer && idx !== selectedPiece)))
					);

					return (
						<g
							key={idx}
							onClick={() => handleClick(idx)}
							style={{ cursor: isClickable ? 'pointer' : 'default' }}
						>
							{/* Square */}
							<rect
								x={x}
								y={y}
								width={CELL}
								height={CELL}
								fill={squareFill}
								stroke={isSelected ? '#16a34a' : '#8b7355'}
								strokeWidth={isSelected ? 3 : 0.5}
							/>

							{/* Piece */}
							{(cell === 'white' || cell === 'black') && (
								<circle
									cx={x + CELL / 2}
									cy={y + CELL / 2}
									r={36}
									fill={cell === 'white' ? '#f8fafc' : '#1e293b'}
									stroke={cell === 'white' ? '#64748b' : '#94a3b8'}
									strokeWidth={3}
								/>
							)}

							{/* Valid-move dot on empty squares */}
							{isValid && cell === null && (
								<circle
									cx={x + CELL / 2}
									cy={y + CELL / 2}
									r={10}
									fill="rgba(0,0,0,0.2)"
								/>
							)}
						</g>
					);
				})}
			</svg>
			<button className="new-game-button" onClick={resetGame}>
				New Game
			</button>
		</div>
	);
};

export default Breakthrough;
